// Lead Service for managing contact leads with Firestore persistence
import { db } from '../../firebase';
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';

export const leadService = {
    // Get all leads (ordered by newest first)
    async getLeads(filters = {}) {
        try {
            const leadsRef = collection(db, 'leads');
            const q = query(leadsRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            const leads = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                leads.push({
                    id: docSnap.id,
                    ...data,
                    createdAt: data.createdAt?.toDate()?.toISOString() || new Date().toISOString()
                });
            });

            return { success: true, data: leads };
        } catch (error) {
            console.error('Error fetching leads:', error);
            // Fallback to empty if collection doesn't exist yet
            return { success: true, data: [] };
        }
    },

    // Get lead by ID
    async getLeadById(id) {
        try {
            const leadRef = doc(db, 'leads', id);
            const leadSnap = await getDoc(leadRef);

            if (leadSnap.exists()) {
                const data = leadSnap.data();
                return {
                    success: true,
                    data: {
                        id: leadSnap.id,
                        ...data,
                        createdAt: data.createdAt?.toDate()?.toISOString()
                    }
                };
            }
            return { success: false, error: 'Lead not found' };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // Create lead (from contact or insurance form)
    async createLead(leadData) {
        try {
            const leadsRef = collection(db, 'leads');
            const newLead = {
                ...leadData,
                status: 'new',
                createdAt: serverTimestamp()
            };

            const docRef = await addDoc(leadsRef, newLead);
            return { success: true, data: { id: docRef.id, ...leadData } };
        } catch (error) {
            console.error('Error creating lead:', error);
            return { success: false, error: error.message };
        }
    },

    // Update lead status
    async updateLeadStatus(id, status) {
        try {
            const leadRef = doc(db, 'leads', id);
            await updateDoc(leadRef, {
                status,
                updatedAt: serverTimestamp()
            });
            return { success: true, data: { id, status } };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // Delete lead
    async deleteLead(id) {
        try {
            const leadRef = doc(db, 'leads', id);
            await deleteDoc(leadRef);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // Add note to lead
    async addNote(leadId, note) {
        try {
            const leadRef = doc(db, 'leads', leadId);
            const leadSnap = await getDoc(leadRef);
            const existingNotes = leadSnap.data()?.notes || [];

            await updateDoc(leadRef, {
                notes: [...existingNotes, { text: note, date: new Date().toISOString() }],
                updatedAt: serverTimestamp()
            });
            return { success: true, data: { leadId, note } };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }
};

export default leadService;
